import { Box, Button, MenuItem, Select, Theme, Tooltip, Typography } from "@mui/material";
import {
  IVacationAwardingCrew,
  IVacationAwardingLeftoverChange,
  IVacationBidSlot,
  VacationBidRound,
} from "@vmosolutions/oc-interfaces";

import { VACATION_BID_TEST_ID } from "../testIds";
import { displaySlotId } from "../helpers";
import {
  ILeftoverTarget,
  leftoverChoices,
  leftoverPlacement,
  leftoverTargets,
  sideLabel,
  slotDateRange,
} from "./awardingHelpers";

/** Where the days are to go, or null to take them off whichever block holds them. */
export type LeftoverPlacement = IVacationAwardingLeftoverChange["placement"];

export interface ICrewLeftoverDaysProps {
  crew: IVacationAwardingCrew;
  /** Which round is being awarded. Crew only name a placement in round 2, so round 1 cannot say. */
  round: VacationBidRound;
  slotsById: Map<string, IVacationBidSlot>;
  showSlotIdYear: boolean;
  /** False when the round is read-only; every action is then disabled. */
  writesAllowed: boolean;
  /** A leftover-day change for this crew member is still being saved. */
  pending: boolean;
  /** Slots whose removal is still being saved; the days cannot be put on one of those. */
  pendingRemoves: Set<string>;
  onPlace: (placement: LeftoverPlacement) => void;
}

/** Nothing placed, as the select's value. */
const NOT_PLACED = "";

const targetValue = (target: Pick<ILeftoverTarget, "slotId" | "side">): string => `${target.slotId} ${target.side}`;

const CrewLeftoverDaysStyles = (theme: Theme): any => ({
  "& .leftover_label": {
    fontSize: 11,
    textTransform: "uppercase",
    color: theme.custom.forms.labels,
    margin: "12px 0 4px",
    "& .leftover_label_hint": { textTransform: "none", marginLeft: "6px" },
  },
  "& .leftover_placed": { fontSize: 13, color: theme.palette.success.main },
  "& .leftover_unplaced": { fontSize: 13, color: theme.palette.warning.light },
  "& .leftover_empty": { color: theme.custom.forms.labels, fontSize: 13 },
  "& .leftover_choice": {
    display: "grid",
    gridTemplateColumns: "28px 104px 120px 60px minmax(0, 1fr) auto",
    alignItems: "center",
    columnGap: "12px",
    minHeight: "32px",
    padding: "2px 10px",
    "& .rank": { color: theme.custom.forms.labels },
    "& .slot_id": { fontWeight: 700, whiteSpace: "nowrap" },
    "& .state": { color: theme.custom.forms.labels },
    "& .state.placed": { color: theme.palette.success.main },
    "& .state.live": { color: theme.palette.warning.light },
    "& .MuiButtonBase-root": { padding: "1px 10px", fontSize: 12, minWidth: 0 },
  },
  "& .leftover_control": {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: "6px",
    padding: "0 10px",
    "& .MuiInputBase-root": { fontSize: 13, minWidth: "260px" },
  },
});

/**
 * One crew member's leftover days: where they asked for them, and where the scheduler has put them.
 *
 * The days hang off a block the crew member holds, so the choices they made at bid time are only
 * offers - their first choice often goes to someone more senior, and the scheduler then places the
 * days themselves.
 */
export const CrewLeftoverDays = (props: ICrewLeftoverDaysProps): JSX.Element | null => {
  const { crew, round, slotsById, showSlotIdYear, writesAllowed, pending, pendingRemoves, onPlace } = props;

  const placement = leftoverPlacement(crew);
  const entitled = crew.entitlement.leftoverDays;
  // Nothing to place and nothing placed: an entitlement that divides evenly into blocks, or one
  // whose blocks cannot be counted at all.
  if (!entitled && !placement) return null;

  const choices = leftoverChoices(crew, pendingRemoves);
  const targets = leftoverTargets(crew, slotsById, pendingRemoves);
  const disabled = !writesAllowed || pending;
  // The placed days are what the server stored, which an entitlement correction can since have moved.
  const days = placement?.days ?? entitled ?? 0;
  const dayCount = `${days} day${days === 1 ? "" : "s"}`;

  // Every disabled control below says why, in the same idiom as the award button.
  const placeBlockedReason = !writesAllowed
    ? "This round can no longer be changed"
    : pending
      ? "Their last leftover-day change is still saving"
      : !targets.length
        ? "Award them a block first - the days hang off one they hold"
        : "";

  // The days sit past the block's GVAC, so on back-to-back blocks they would fall on the next one.
  const landsOnReason = (target: Pick<ILeftoverTarget, "landsOn"> | undefined): string =>
    target?.landsOn
      ? `The days would fall on ${displaySlotId(target.landsOn, showSlotIdYear)}, which they also hold`
      : "";

  const choose = (value: string): void => {
    const target = targets.find((z) => targetValue(z) === value);
    onPlace(target ? { slotId: target.slotId, side: target.side } : null);
  };

  return (
    <Box sx={CrewLeftoverDaysStyles} data-testid={VACATION_BID_TEST_ID.crewLeftoverDays}>
      <Typography className="leftover_label">
        Leftover days<span className="leftover_label_hint">go on one block they hold, never split</span>
      </Typography>

      {placement ? (
        <Typography className="leftover_placed">
          {`${dayCount} ${sideLabel(placement.side).toLowerCase()} ${displaySlotId(placement.slotId, showSlotIdYear)}`}
        </Typography>
      ) : (
        <Typography className="leftover_unplaced">{`${dayCount}, not placed yet.`}</Typography>
      )}

      {!choices.length && (
        // There is nowhere to name a placement before round 2, so in round 1 an empty list is not
        // the crew member keeping quiet - it is the only thing their bid could have said.
        <Typography className="leftover_empty">
          {round === VacationBidRound.Round1
            ? "Crew name where their days go when they bid round 2."
            : "They did not say where they would like the days."}
        </Typography>
      )}
      {choices.map((choice) => {
        const slot = slotsById.get(choice.slotId);
        const target = targets.find((z) => targetValue(z) === targetValue(choice));
        const state = choice.placed
          ? "Days are here"
          : choice.live
            ? "Best choice they hold"
            : choice.held
              ? "Holds this block"
              : "Block not awarded";
        return (
          <div
            key={`${choice.slotId} ${choice.side}`}
            className="leftover_choice"
            data-testid={VACATION_BID_TEST_ID.leftoverChoiceRow}
          >
            <Typography variant="body2" className="rank">
              {choice.rank}
            </Typography>
            <Typography variant="body2" className="slot_id">
              {displaySlotId(choice.slotId, showSlotIdYear)}
            </Typography>
            <Typography variant="body2">{slot ? slotDateRange(slot) : ""}</Typography>
            <Typography variant="body2">{sideLabel(choice.side)}</Typography>
            <Typography variant="caption" className={`state${choice.placed ? " placed" : choice.live ? " live" : ""}`}>
              {state}
            </Typography>
            {choice.held && !choice.placed && (
              <Tooltip title={placeBlockedReason || landsOnReason(target)} placement="top">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={disabled || !!target?.landsOn}
                    onClick={() => onPlace({ slotId: choice.slotId, side: choice.side })}
                    data-testid={VACATION_BID_TEST_ID.placeLeftoverButton}
                  >
                    Place here
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
        );
      })}

      <div className="leftover_control">
        <Typography variant="caption">Days go</Typography>
        <Tooltip title={placeBlockedReason} placement="top">
          <span>
            <Select
              size="small"
              displayEmpty={true}
              aria-label="Where the leftover days go"
              disabled={disabled || !targets.length}
              value={placement ? targetValue(placement) : NOT_PLACED}
              onChange={(event) => choose(String(event.target.value))}
              data-testid={VACATION_BID_TEST_ID.leftoverPlacementSelect}
            >
              <MenuItem value={NOT_PLACED}>Nowhere yet</MenuItem>
              {targets.map((target) => (
                <MenuItem key={targetValue(target)} value={targetValue(target)} disabled={!!target.landsOn}>
                  {`${sideLabel(target.side)} ${displaySlotId(target.slotId, showSlotIdYear)}`}
                  {target.requestedRank !== null && ` · their choice ${target.requestedRank}`}
                  {target.landsOn && ` · falls on ${displaySlotId(target.landsOn, showSlotIdYear)}`}
                </MenuItem>
              ))}
            </Select>
          </span>
        </Tooltip>
      </div>
    </Box>
  );
};

export default CrewLeftoverDays;
